import { useState, useEffect } from "react";
import { Modal } from "@excalidraw/excalidraw/components/Modal";
import { Island } from "@excalidraw/excalidraw/components/Island";
import { CloseIcon } from "@excalidraw/excalidraw/components/icons";
import { useDrawings } from "../hooks/useDrawings";
import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { restoreElements, restoreAppState } from "@excalidraw/excalidraw/data/restore";
import { LocalData } from "../data/LocalData";

export const DrawingsModal = ({
  isOpen,
  onClose,
  onLoadDrawing,
}: {
  isOpen: boolean;
  onClose: () => void;
  onLoadDrawing?: (id: number) => void;
}) => {
  const { loadDrawings, loadDrawing, deleteDrawing } = useDrawings();
  const [drawings, setDrawings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const excalidrawAPI = useExcalidrawAPI();

  useEffect(() => {
    if (isOpen) {
      fetchDrawings();
    }
  }, [isOpen]);

  const fetchDrawings = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await loadDrawings();
      setDrawings(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = async (id: number) => {
    try {
      const drawing = await loadDrawing(id);
      if (excalidrawAPI && drawing.data) {
        let parsed;
        try {
          parsed = typeof drawing.data === "string" ? JSON.parse(drawing.data) : drawing.data;
          // Si por alguna razón se guardó doblemente serializado
          if (typeof parsed === "string") {
            parsed = JSON.parse(parsed);
          }
        } catch (e) {
          throw new Error("Datos del dibujo corruptos o inválidos");
        }

        const currentAppState = excalidrawAPI.getAppState();
        const restoredElements = restoreElements(parsed.elements || [], null, { repairBindings: true, deleteInvisibleElements: true });
        const restoredAppState = restoreAppState(parsed.appState || {}, currentAppState);

        // Limpiar la escena actual antes de cargar para evitar conflictos
        excalidrawAPI.resetScene();

        // Actualizar solo los elementos y configuraciones seguras
        excalidrawAPI.updateScene({
          elements: restoredElements,
          appState: {
            isLoading: false,
            name: drawing.name || currentAppState.name,
            viewBackgroundColor: restoredAppState.viewBackgroundColor || currentAppState.viewBackgroundColor,
            theme: restoredAppState.theme || currentAppState.theme,
          }
        });

        try {
          const files = excalidrawAPI.getFiles();
          LocalData.save(restoredElements, excalidrawAPI.getAppState(), files, () => {});
          LocalData.flushSave();
        } catch (e) {
          console.error("Error saving loaded drawing to local storage:", e);
        }

        if (onLoadDrawing) {
          onLoadDrawing(id);
        }
        onClose();
      }
    } catch (err: any) {
      setError(err.message);
      // Intentar forzar que se quite la pantalla de carga en caso de fallo parcial
      if (excalidrawAPI) {
         excalidrawAPI.updateScene({ appState: { isLoading: false } });
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteDrawing(id);
      await fetchDrawings();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal labelledBy="drawings-dialog-title" onCloseRequest={onClose} maxWidth={400}>
      <Island>
        <h2 id="drawings-dialog-title" className="Dialog__title">
          <span className="Dialog__titleContent">Mis Dibujos (Servidor)</span>
        </h2>
        <button
          className="Dialog__close"
          onClick={onClose}
          title="Cerrar"
          aria-label="Cerrar"
          type="button"
        >
          {CloseIcon}
        </button>
        <div className="Dialog__content">
          {error && <div className="alert alert--danger" style={{ padding: "0.5rem", marginBottom: "1rem" }}>{error}</div>}
          {loading ? (
            <p>Cargando...</p>
          ) : drawings.length === 0 ? (
            <p>No tienes dibujos guardados en el servidor.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: "300px", overflowY: "auto" }}>
              {drawings.map((d) => (
                <li key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem", borderBottom: "1px solid var(--default-border-color)" }}>
                  <span style={{ cursor: "pointer", flex: 1, fontWeight: "bold" }} onClick={() => handleLoad(d.id)}>
                    {d.name || "Sin nombre"} <span style={{ fontWeight: "normal", fontSize: "0.8em", color: "var(--text-color-secondary)" }}>({new Date(d.updated_at).toLocaleDateString()})</span>
                  </span>
                  <button onClick={() => handleDelete(d.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-danger)", padding: "0.25rem 0.5rem" }}>
                    Borrar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Island>
    </Modal>
  );
};
